import React, { useEffect, useState } from 'react';
import { supportFetch, supportDownload, fmtBytes, fmtDate } from '../api';
import ExportButton from '../components/ExportButton';
import ProgressBar from '../../Common/ProgressBar/ProgressBar';

// pocketReader session logs (.log.txt). The new upload flow splits each upload's
// trailing troubleshooting log into its own support-only blob; this tab lists them
// and lets support download an individual log for troubleshooting.
//
// Filtering mirrors the Back End Logs tab (LogsTab) — same sp-filterbar UI — but
// with the fields these file-based logs carry (company, serial, filename, upload
// date range). There is no level/type here, so those two event-log filters are
// dropped. The list is loaded once, so filtering is client-side and instant.
const PocketReaderLogsTab = ({ token, onAuthFail }) => {
  const [logs, setLogs]       = useState([]);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState('');   // blob name currently downloading
  const [percent, setPercent] = useState(0);    // 0..100, or null when size unknown
  const [filters, setFilters] = useState({ company: '', serial: '', search: '', from: '', to: '' });

  useEffect(() => {
    supportFetch('/support/pocketreader-logs', token, onAuthFail)
      .then(d => setLogs(d.logs || []))
      .catch(e => { if (e.message !== 'Session expired') setError('Failed to load pocketReader logs.'); })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = (log) => {
    setBusy(log.name); setPercent(0); setError('');
    supportDownload(`/support/download/${encodeURI(log.name)}`, token, onAuthFail, setPercent)
      .catch(e => { if (e.message !== 'Session expired') setError(`Download failed: ${log.name}`); })
      .finally(() => setBusy(''));
  };

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  if (loading) return <div className="sp-loading">Loading pocketReader logs…</div>;
  if (error && logs.length === 0) return <div className="sp-error">{error}</div>;

  const has = (v, q) => String(v).toLowerCase().includes(q.toLowerCase());
  const filtered = logs.filter(l => {
    if (filters.company && !has(l.company, filters.company)) return false;
    if (filters.serial  && !has(l.serial, filters.serial))   return false;
    if (filters.search  && !has(`${l.company} ${l.serial} ${l.name}`, filters.search)) return false;
    if (filters.from && new Date(l.uploadedAt) < new Date(filters.from)) return false;
    if (filters.to   && new Date(l.uploadedAt) > new Date(filters.to))   return false;
    return true;
  });

  // Trim the company/ prefix for a friendlier filename display; full name is in the title.
  const shortName = (name) => name.includes('/') ? name.slice(name.indexOf('/') + 1) : name;

  const exportRows = filtered.map(l => ({
    company: l.company, serial: l.serial, file: l.name, kind: l.kind, uploadedAt: l.uploadedAt, bytes: l.bytes,
  }));

  const kindLabel = (k) => k === 'text' ? 'Text file' : 'Session log';

  return (
    <div className="sp-pocketreader-logs">
      <form className="sp-filterbar" onSubmit={e => e.preventDefault()}>
        <input placeholder="Company" value={filters.company} onChange={e => set('company', e.target.value)} />
        <input placeholder="Serial"  value={filters.serial}  onChange={e => set('serial', e.target.value)} />
        <input placeholder="Search file…" value={filters.search} onChange={e => set('search', e.target.value)} />
        <label className="sp-date">From <input type="datetime-local" value={filters.from} onChange={e => set('from', e.target.value)} /></label>
        <label className="sp-date">To <input type="datetime-local" value={filters.to} onChange={e => set('to', e.target.value)} /></label>
        <button className="sp-btn" type="button" onClick={() => setFilters({ company: '', serial: '', search: '', from: '', to: '' })}>Clear</button>
        <ExportButton rows={exportRows} name="pocketreader-logs" />
      </form>

      {error && <div className="sp-error">{error}</div>}

      {filtered.length === 0 ? (
        <div className="sp-empty">{logs.length === 0 ? 'No pocketReader logs yet.' : 'No logs match.'}</div>
      ) : (
        <table className="sp-table sp-table--compact">
          <thead><tr><th>Company</th><th>Serial</th><th>File</th><th>Type</th><th>Uploaded</th><th>Size</th><th></th></tr></thead>
          <tbody>
            {filtered.map(l => (
              <tr key={l.name}>
                <td>{l.company}</td>
                <td>{l.serial}</td>
                <td title={l.name}>{shortName(l.name)}</td>
                <td><span className="sp-badge">{kindLabel(l.kind)}</span></td>
                <td>{fmtDate(l.uploadedAt)}</td>
                <td>{fmtBytes(l.bytes)}</td>
                <td>
                  <button className="sp-btn" disabled={busy === l.name} onClick={() => handleDownload(l)}>
                    {busy === l.name ? 'Downloading…' : 'Download'}
                  </button>
                  {busy === l.name && (
                    <div className="download-progress">
                      <ProgressBar barHeight={6} progressPercentage={percent} />
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="sp-toolbar">
        <span className="sp-muted">{filtered.length} log{filtered.length !== 1 ? 's' : ''}{filtered.length !== logs.length && ` of ${logs.length}`}</span>
      </div>
    </div>
  );
};

export default PocketReaderLogsTab;
